import React from 'react';
import { ContentRendererProps } from './types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InfoIcon, Target, DollarSign, Users, Camera, Copy } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Renderer for ad group content type
 */
export const AdGroupRenderer: React.FC<ContentRendererProps> = ({ 
  content
}) => {
  // Extract ad group specific data with defaults
  const { 
    name: adGroupName, 
    platform = 'google',
    ads = [], 
    settings = {},
    targeting = {},
    // Instagram/social media specific fields
    descriptions = [],
    short_headlines = [],
    bonus_metadata = {}
  } = content.content_json || {};

  // Determine platform from content name or other indicators
  const determinePlatform = () => {
    if (content.name?.toLowerCase().includes('instagram')) return 'instagram';
    if (content.name?.toLowerCase().includes('facebook')) return 'facebook';
    if (content.name?.toLowerCase().includes('google')) return 'google';
    if (content.name?.toLowerCase().includes('linkedin')) return 'linkedin';
    return platform;
  };

  const actualPlatform = determinePlatform();

  // Helper function to copy text to clipboard
  const copyToClipboard = async (text: string, label: string = 'Text') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`, {
        duration: 2000,
        position: 'bottom-center'
      });
    } catch (err) {
      toast.error('Failed to copy to clipboard', {
        duration: 2000,
        position: 'bottom-center'
      });
    }
  };

  // Render individual Google ad
  const renderGoogleAd = (ad: any, index: number) => {
    return (
      <Card key={ad.id || index} className="mb-4">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-medium">Responsive Search Ad {index + 1}</h4>
            <Badge variant="outline">Google Ads</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Headlines */}
          <div>
            <div className="text-xs text-muted-foreground mb-1">Headlines</div>
            <div className="space-y-1">
              {ad.headlines?.map((headline: any, idx: number) => (
                <div 
                  key={idx} 
                  className="text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 rounded px-2 py-1 transition-colors group flex items-center"
                  onClick={() => copyToClipboard(headline.text, 'Headline')}
                  title="Click to copy"
                >
                  {idx === 0 && <span className="text-muted-foreground mr-1">•</span>}
                  {idx > 0 && <span className="text-muted-foreground mr-1">|</span>}
                  <span className={idx === 0 ? "font-medium flex-1" : "flex-1"}>{headline.text}</span>
                  <Copy className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
                </div>
              )) || <span className="text-muted-foreground">No headlines</span>}
            </div>
          </div>

          {/* Descriptions */}
          <div>
            <div className="text-xs text-muted-foreground mb-1">Descriptions</div>
            <div className="space-y-1">
              {ad.descriptions?.map((desc: any, idx: number) => (
                <div 
                  key={idx} 
                  className="text-sm text-muted-foreground cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 rounded px-2 py-1 transition-colors group flex items-center"
                  onClick={() => copyToClipboard(desc.text, 'Description')}
                  title="Click to copy"
                >
                  <span className="flex-1">{desc.text}</span>
                  <Copy className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
                </div>
              )) || <span className="text-muted-foreground">No descriptions</span>}
            </div>
          </div>

          {/* URL Information */}
          {(ad.path || ad.finalUrl) && (
            <div className="pt-2 border-t">
              {ad.path && (
                <div 
                  className="text-xs cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 rounded px-2 py-1 transition-colors"
                  onClick={() => copyToClipboard(ad.path, 'Display Path')}
                  title="Click to copy"
                >
                  <span className="text-muted-foreground">Display Path:</span> 
                  <span className="ml-1 text-blue-600">{ad.path}</span>
                </div>
              )}
              {ad.finalUrl && (
                <div 
                  className="text-xs mt-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 rounded px-2 py-1 transition-colors"
                  onClick={() => copyToClipboard(ad.finalUrl, 'Final URL')}
                  title="Click to copy"
                >
                  <span className="text-muted-foreground">Final URL:</span> 
                  <span className="ml-1 text-blue-600 truncate inline-block max-w-xs">{ad.finalUrl}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Render individual Facebook ad
  const renderFacebookAd = (ad: any, index: number) => {
    return (
      <Card key={ad.id || index} className="mb-4">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-medium">Facebook Ad {index + 1}</h4>
            <Badge variant="outline">Facebook</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Ad Preview */}
          <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
            {ad.imageUrl && (
              <div className="mb-3 rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-800" style={{ height: '200px' }}>
                <img 
                  src={ad.imageUrl} 
                  alt="Ad preview" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <div className="text-xs text-blue-600 font-medium">SPONSORED</div>
              <div 
                className="font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 rounded px-2 py-1 transition-colors"
                onClick={() => copyToClipboard(ad.headline || 'No headline', 'Headline')}
                title="Click to copy"
              >
                {ad.headline || 'No headline'}
              </div>
              <div 
                className="text-sm text-muted-foreground cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 rounded px-2 py-1 transition-colors"
                onClick={() => copyToClipboard(ad.body || 'No body text', 'Body text')}
                title="Click to copy"
              >
                {ad.body || 'No body text'}
              </div>
              
              {ad.cta && (
                <div className="pt-2">
                  <div 
                    className="inline-block bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium cursor-pointer hover:bg-blue-700 transition-colors"
                    onClick={() => copyToClipboard(ad.cta, 'CTA')}
                    title="Click to copy"
                  >
                    {ad.cta}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render LinkedIn ad (placeholder for now)
  const renderLinkedInAd = (ad: any, index: number) => {
    return (
      <Card key={ad.id || index} className="mb-4">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-medium">LinkedIn Ad {index + 1}</h4>
            <Badge variant="outline">LinkedIn</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            {JSON.stringify(ad, null, 2)}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render Instagram ad format (with descriptions and short_headlines)
  const renderInstagramContent = () => {
    return (
      <div className="space-y-6">
        {/* Headlines Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Headlines</CardTitle>
            <p className="text-sm text-muted-foreground">
              Short, impactful headlines for your Instagram ads
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {short_headlines.map((headline: string, idx: number) => (
                <div 
                  key={idx} 
                  className="p-3 border rounded-lg bg-gray-50 dark:bg-gray-900 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group"
                  onClick={() => copyToClipboard(headline, 'Headline')}
                  title="Click to copy"
                >
                  <div className="flex items-start justify-between">
                    <span className="text-sm font-medium">{headline}</span>
                    <Copy className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-1" />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {headline.length} chars
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Descriptions Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Descriptions</CardTitle>
            <p className="text-sm text-muted-foreground">
              Compelling descriptions to engage your audience
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {descriptions.map((desc: string, idx: number) => (
                <div 
                  key={idx} 
                  className="p-4 border rounded-lg bg-gray-50 dark:bg-gray-900 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group"
                  onClick={() => copyToClipboard(desc, 'Description')}
                  title="Click to copy"
                >
                  <div className="flex items-start justify-between">
                    <p className="text-sm flex-1">{desc}</p>
                    <Copy className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2" />
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    {desc.length} characters
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Bonus Metadata Section */}
        {bonus_metadata && Object.keys(bonus_metadata).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Additional Metadata</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {bonus_metadata.cta_suggestions && (
                <div>
                  <h4 className="font-medium mb-2">CTA Suggestions</h4>
                  <div className="flex flex-wrap gap-2">
                    {bonus_metadata.cta_suggestions.map((cta: string, idx: number) => (
                      <Badge 
                        key={idx} 
                        variant="secondary"
                        className="cursor-pointer hover:bg-secondary/80 transition-colors"
                        onClick={() => copyToClipboard(cta, 'CTA')}
                      >
                        {cta}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {bonus_metadata.search_keywords && (
                <div>
                  <h4 className="font-medium mb-2">Search Keywords</h4>
                  <div className="flex flex-wrap gap-2">
                    {bonus_metadata.search_keywords.map((keyword: string, idx: number) => (
                      <Badge 
                        key={idx} 
                        variant="outline"
                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        onClick={() => copyToClipboard(keyword, 'Keyword')}
                      >
                        {keyword}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {bonus_metadata.negative_keywords && (
                <div>
                  <h4 className="font-medium mb-2">Negative Keywords</h4>
                  <div className="flex flex-wrap gap-2">
                    {bonus_metadata.negative_keywords.map((keyword: string, idx: number) => (
                      <Badge 
                        key={idx} 
                        variant="destructive" 
                        className="opacity-70 cursor-pointer hover:opacity-100 transition-opacity"
                        onClick={() => copyToClipboard(keyword, 'Negative keyword')}
                      >
                        {keyword}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {bonus_metadata.audience_targeting_suggestions && (
                <div>
                  <h4 className="font-medium mb-2">Audience Targeting</h4>
                  <div className="flex flex-wrap gap-2">
                    {bonus_metadata.audience_targeting_suggestions.map((audience: string, idx: number) => (
                      <Badge 
                        key={idx} 
                        variant="secondary"
                        className="cursor-pointer hover:bg-secondary/80 transition-colors"
                        onClick={() => copyToClipboard(audience, 'Audience')}
                      >
                        {audience}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // Get platform-specific icon
  const getPlatformIcon = () => {
    switch (actualPlatform) {
      case 'google':
        return <Target className="h-4 w-4" />;
      case 'facebook':
        return <Users className="h-4 w-4" />;
      case 'instagram':
        return <Camera className="h-4 w-4" />;
      case 'linkedin':
        return <Users className="h-4 w-4" />;
      default:
        return <Target className="h-4 w-4" />;
    }
  };

  // Get platform display name
  const getPlatformName = () => {
    switch (actualPlatform) {
      case 'google':
        return 'Google Ads';
      case 'facebook':
        return 'Facebook Ads';
      case 'instagram':
        return 'Instagram Ads';
      case 'linkedin':
        return 'LinkedIn Ads';
      default:
        return actualPlatform.charAt(0).toUpperCase() + actualPlatform.slice(1);
    }
  };

  return (
    <div className="w-full h-full overflow-auto bg-white dark:bg-gray-950 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            {getPlatformIcon()}
            <h2 className="text-2xl font-bold">{adGroupName || content.name || 'Untitled Ad Group'}</h2>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <Badge variant="secondary">{getPlatformName()}</Badge>
            {actualPlatform === 'instagram' ? (
              <span>
                {short_headlines.length} headline{short_headlines.length !== 1 ? 's' : ''}, 
                {' '}{descriptions.length} description{descriptions.length !== 1 ? 's' : ''}
              </span>
            ) : (
              <span>{ads.length} ad{ads.length !== 1 ? 's' : ''}</span>
            )}
            {content.created_at && (
              <span>Created {new Date(content.created_at).toLocaleDateString()}</span>
            )}
          </div>
        </div>

        {/* Content */}
        {/* For Instagram, check if we have descriptions/headlines instead of ads array */}
        {actualPlatform === 'instagram' && (descriptions.length > 0 || short_headlines.length > 0) ? (
          renderInstagramContent()
        ) : ads.length === 0 ? (
          <Alert>
            <InfoIcon className="h-4 w-4" />
            <AlertDescription>
              No ads have been created for this ad group yet. Use the editor to add ads.
            </AlertDescription>
          </Alert>
        ) : (
          <Tabs defaultValue="ads" className="w-full">
            <TabsList>
              <TabsTrigger value="ads">Ad Variations</TabsTrigger>
              <TabsTrigger value="performance">Performance</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="ads" className="mt-6">
              <div>
                {ads.map((ad: any, index: number) => {
                  switch (actualPlatform) {
                    case 'google':
                      return renderGoogleAd(ad, index);
                    case 'facebook':
                      return renderFacebookAd(ad, index);
                    case 'linkedin':
                      return renderLinkedInAd(ad, index);
                    default:
                      return (
                        <Card key={index} className="mb-4">
                          <CardContent className="pt-6">
                            <pre className="text-sm overflow-auto">
                              {JSON.stringify(ad, null, 2)}
                            </pre>
                          </CardContent>
                        </Card>
                      );
                  }
                })}
              </div>
            </TabsContent>

            <TabsContent value="performance" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Performance Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-muted-foreground">
                    <DollarSign className="h-12 w-12 mx-auto mb-2 opacity-20" />
                    <p>Performance data will be displayed here once the ads are running.</p>
                    <p className="text-sm mt-2">Connect your {getPlatformName()} account to view metrics.</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="settings" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Ad Group Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Display targeting info if available */}
                  {Object.keys(targeting).length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Targeting</h4>
                      <pre className="text-sm bg-gray-100 dark:bg-gray-900 p-3 rounded overflow-auto">
                        {JSON.stringify(targeting, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Display settings if available */}
                  {Object.keys(settings).length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Additional Settings</h4>
                      <pre className="text-sm bg-gray-100 dark:bg-gray-900 p-3 rounded overflow-auto">
                        {JSON.stringify(settings, null, 2)}
                      </pre>
                    </div>
                  )}

                  {Object.keys(targeting).length === 0 && Object.keys(settings).length === 0 && (
                    <div className="text-center py-4 text-muted-foreground">
                      <p>No additional settings configured for this ad group.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default AdGroupRenderer;